import { AxiosError } from 'axios'
import { PaytrieAPI } from '@paytrie/client'
import {
  ArrowLeft,
  Mail,
  AlertCircle,
  CheckCircle,
  Loader2,
  Phone,
  Info,
  Briefcase,
  Clock,
  RefreshCw,
} from 'lucide-react'
import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import QRCode from 'react-qr-code'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { PlacesAutocompleteInput } from '@/components/ui/places-autocomplete'
import { useGooglePlaces } from '@/hooks/useGooglePlaces'
import { usePasswordlessAuth } from '@/hooks/usePasswordlessAuth'
import { authService } from '@/services/auth'
import {
  isValidDate,
  validateAge,
  validateName,
  validateOccupationDetails,
} from '@/lib/utils'
import { isMobile } from '@/utils/isMobile'
import { logger } from '@/utils/logger'
import { trackMetaEvent } from '@/utils/metaPixel'
import {
  employmentOptions,
  occupationOptions,
  provinceOptions,
  purposeOptions,
  SignUpStep,
} from '@/utils/signup'
import { startKycRedirect } from '@/utils/kyc'
import {
  patchOccupationInfo,
  patchOlderUserInfo,
  patchOnboardingSource,
  patchPersonalInfo,
  sendSMS,
  updateCurrentFlow,
  verifySMS,
} from '@/utils/signupApi'
import { configurePaytrieClient } from '@/utils/apiClient'
import {
  hasPendingOnboarding,
  resolveStepFromRequirements,
} from '@/utils/signupResume'
import { formatPostalCode, validateEmail } from '@/utils/validation'

import { Label } from '../ui/label'
import { RadioGroup, RadioGroupItem } from '../ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

interface SignUpScreenProps {
  onBack: () => void
  onSignUpSuccess: (email: string) => void
  onSignIn?: () => void
}

const stepOrder: SignUpStep[] = [
  'email',
  'phone',
  'verify-phone',
  'personal-info',
  'occupation',
  'additional-info',
  'additional-info-2',
  'start-kyc',
]

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

const SignUpScreen = ({
  onBack,
  onSignIn,
  onSignUpSuccess,
}: SignUpScreenProps) => {
  const urlReferralCode = new URLSearchParams(window.location.search).get('ref')

  const [city, setCity] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [postal, setPostal] = useState('')
  const [dobDay, setDobDay] = useState('')
  const [dobYear, setDobYear] = useState('')
  const [province, setProvince] = useState('')
  const [address1, setAddress1] = useState('')
  const [address2, setAddress2] = useState('')
  const [dobMonth, setDobMonth] = useState('')
  const [lastName, setLastName] = useState('')
  const [occupation, setOccupation] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingEmail, setIsCheckingEmail] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<string>('')
  const [intendedPurpose, setIntendedPurpose] = useState('')
  const [success, setSuccess] = useState<string | null>(null)
  const [employmentStatus, setEmploymentStatus] = useState('')
  const [occupationDetails, setOccupationDetails] = useState('')
  const [isParsingAddress, setIsParsingAddress] = useState(false)
  const [personalInfoStep, setPersonalInfoStep] = useState<1 | 2>(1)
  const [currentStep, setCurrentStep] = useState<SignUpStep>('email')
  const [isProcessingSuccess, setIsProcessingSuccess] = useState(false)
  const [referralCode, setReferralCode] = useState(urlReferralCode ?? '')
  const [verificationUrl, setVerificationUrl] = useState<string | null>(null)
  const [phoneCode, setPhoneCode] = useState('')
  const [firstName, setFirstName] = useState('')
  const [sourceOfFunds, setSourceOfFunds] = useState('')
  const [intendedUseOfUSDC, setIntendedUseOfUSDC] = useState('')
  const [walletProvider, setWalletProvider] = useState('')
  const [walletAddress, setWalletAddress] = useState('')
  const [howDidYouHear, setHowDidYouHear] = useState('')
  const [whoReferredYou, setWhoReferredYou] = useState('')
  const [previousCryptoExperience, setPreviousCryptoExperience] = useState('')
  const hasResumedOnboardingRef = useRef(false)
  const [needsAdditionalInfo, setNeedsAdditionalInfo] = useState(false)

  useEffect(() => {
    if (!authService.isAuthenticated() || hasResumedOnboardingRef.current) {
      return
    }

    hasResumedOnboardingRef.current = true
    configurePaytrieClient()

    PaytrieAPI.getCurrentUserRequirements()
      .then((requirementsRes) => {
        if (requirementsRes.status !== 200) return

        const requirements = requirementsRes.data.data
        if (!hasPendingOnboarding(requirements)) return

        const nextStep = resolveStepFromRequirements(requirements)
        if (!nextStep) return

        if (nextStep === 'additional-info') {
          setNeedsAdditionalInfo(true)
        }

        const authEmail = authService.getAuthData()?.email
        if (authEmail) setEmail(authEmail)
        setCurrentStep(nextStep)
      })
      .catch((err) => {
        logger.error('Failed to resume signup from requirements', err)
      })
  }, [])

  const handleApiError = useCallback((err: unknown) => {
    if (err instanceof AxiosError) {
      const errorData = err.response?.data
      const errorMessage =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message ||
            err.response?.statusText ||
            'An unexpected error occurred. Please try again.'
      setError(errorMessage)
    } else if (err instanceof Error) {
      setError(err.message)
    } else {
      setError(
        'A network error occurred. Please check your connection and try again.'
      )
    }
  }, [])

  const clearMessages = useCallback(() => {
    setError(null)
    setSuccess(null)
  }, [])

  const continueSignupAfterAuth = useCallback(
    async (verifiedEmail: string) => {
      const requirementsRes = await PaytrieAPI.getCurrentUserRequirements()
      if (requirementsRes.status !== 200) {
        throw new Error(
          'Unable to determine your signup progress. Please try again.'
        )
      }

      const requirements = requirementsRes.data.data
      if (!hasPendingOnboarding(requirements)) {
        setIsCheckingEmail(false)
        onSignUpSuccess(verifiedEmail)
        return
      }

      const nextStep = resolveStepFromRequirements(requirements)
      if (!nextStep) {
        throw new Error(
          'Unable to determine your signup progress. Please try again.'
        )
      }

      if (nextStep === 'additional-info') {
        setNeedsAdditionalInfo(true)
      }

      trackMetaEvent('Lead')
      setSuccess('Email verified successfully!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        setCurrentStep(nextStep)
        setSuccess(null)
        setIsProcessingSuccess(false)
        setIsCheckingEmail(false)
      }, 1500)
    },
    [onSignUpSuccess]
  )

  const retryAfterEmailVerified = useCallback(async () => {
    const authEmail = authService.getAuthData()?.email
    if (!authEmail || !authService.isAuthenticated()) {
      setIsCheckingEmail(false)
      return
    }

    clearMessages()
    setIsCheckingEmail(true)

    try {
      const onboardingResult = await patchOnboardingSource('gmt')
      if (onboardingResult.ok === false) {
        logger.warn('Failed to record onboarding source; continuing signup', {
          message: onboardingResult.message,
        })
      }

      await continueSignupAfterAuth(authEmail)
    } catch (err) {
      handleApiError(err)
    }
  }, [clearMessages, continueSignupAfterAuth, handleApiError])

  const handleEmailVerified = useCallback(
    async ({
      email: verifiedEmail,
      accessToken,
    }: {
      email: string
      accessToken: string
    }) => {
      setIsCheckingEmail(true)
      authService.setToken(accessToken, verifiedEmail)
      configurePaytrieClient()

      try {
        const onboardingResult = await patchOnboardingSource('gmt')
        if (onboardingResult.ok === false) {
          logger.warn('Failed to record onboarding source; continuing signup', {
            message: onboardingResult.message,
          })
        }

        await continueSignupAfterAuth(verifiedEmail)
      } catch (err) {
        handleApiError(err)
      }
    },
    [continueSignupAfterAuth, handleApiError]
  )

  const {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading: magicLinkLoading,
    errorMessage: magicLinkError,
    resetDenied,
    pollTimeoutMs,
  } = usePasswordlessAuth({
    client: 'gmt',
    onVerified: handleEmailVerified,
  })

  useEffect(() => {
    if (!waitingForApproval || timeRemaining <= 0) return

    const interval = setInterval(() => {
      setTimeRemaining((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)

    return () => clearInterval(interval)
  }, [waitingForApproval, timeRemaining])

  // Google Places integration for address autocomplete
  const { isLoaded, loadError, parseAddressComponents, getSearchOptions } =
    useGooglePlaces()

  const dob = useMemo(() => {
    if (
      dobYear &&
      dobMonth &&
      dobDay &&
      isValidDate(dobYear, dobMonth, dobDay)
    ) {
      return `${dobYear}-${dobMonth.padStart(2, '0')}-${dobDay.padStart(2, '0')}`
    }
    return ''
  }, [dobYear, dobMonth, dobDay])

  const handleYearChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    if (numericValue.length <= 4) {
      setDobYear(numericValue)
      // Auto-focus month when year is complete (4 digits)
      if (numericValue.length === 4) {
        const monthInput = document.getElementById('dobMonth')
        monthInput?.focus()
      }
    }
  }

  const handleMonthChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    if (numericValue.length <= 2) {
      setDobMonth(numericValue)
      // Auto-focus day when month is complete (2 digits)
      if (numericValue.length === 2) {
        const dayInput = document.getElementById('dobDay')
        dayInput?.focus()
      }
    }
  }

  const handleDayChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '')
    if (numericValue.length <= 2) {
      setDobDay(numericValue)
    }
  }

  // Account purpose validation
  const validateAccountPurpose = (purpose: string) => {
    if (!purpose || purpose.trim().length === 0) {
      return {
        isValid: false,
        error: 'Please select your intended account purpose',
      }
    }
    return { isValid: true, error: '' }
  }

  // Handle first name change with validation
  const handleFirstNameChange = (value: string) => {
    // Allow typing but filter out numbers
    const filteredValue = value.replace(/[0-9]/g, '')
    setFirstName(filteredValue)
  }

  // Handle last name change with validation
  const handleLastNameChange = (value: string) => {
    // Allow typing but filter out numbers
    const filteredValue = value.replace(/[0-9]/g, '')
    setLastName(filteredValue)
  }

  // Handle address selection from Places Autocomplete
  const handleAddressSelect = useCallback(
    async (placeId: string) => {
      setIsParsingAddress(true)
      try {
        // Parse address for Canada (default for signup)
        // Note: address1 will already be set to the full address by the PlacesAutocompleteInput component
        const parsed = await parseAddressComponents(placeId, 'CA')
        if (parsed) {
          // Keep the full address in address1 (don't change it to avoid re-triggering dropdown)
          // Just update the other fields
          setAddress2(parsed.unit || '')
          setCity(parsed.city)
          setProvince(parsed.stateProvince.toLowerCase()) // Convert to lowercase for province selector
          setPostal(formatPostalCode(parsed.postalCode, 'CA').replace(' ', '')) // Remove space for backend
        }
      } catch (error) {
        logger.error('Failed to parse address', error)
      } finally {
        setIsParsingAddress(false)
      }
    },
    [parseAddressComponents]
  )

  const handleBackButton = () => {
    // Special handling for personal-info step with substeps
    if (currentStep === 'personal-info' && personalInfoStep === 2) {
      setPersonalInfoStep(1)
      clearMessages()
      return
    }

    if (currentStep === 'additional-info-2') {
      setCurrentStep('additional-info')
      clearMessages()
      return
    }

    if (currentStep === 'start-kyc') {
      setCurrentStep(needsAdditionalInfo ? 'additional-info-2' : 'occupation')
      clearMessages()
      return
    }

    if (currentStep === 'additional-info') {
      setCurrentStep('occupation')
      clearMessages()
      return
    }

    const currentIndex = stepOrder.indexOf(currentStep)

    if (currentIndex > 0) {
      // Go to previous step in signup flow
      setCurrentStep(stepOrder[currentIndex - 1])
      clearMessages()
      // Reset personal info substep when going back to personal-info
      if (stepOrder[currentIndex - 1] === 'personal-info') {
        setPersonalInfoStep(1)
      }
    } else {
      // On first step, go back to send money page
      onBack()
    }
  }

  const handleStartEmailSignup = async () => {
    const validation = validateEmail(email)
    if (!validation.isValid) {
      setEmailError(validation.error)
      return
    }
    clearMessages()
    setEmailError('')

    try {
      window.clarity?.('set', 'email', email.trim())
    } catch {
      //
    }

    const sendSucceeded = await sendMagicLink({
      email: email.trim(),
      referral: referralCode.trim() || undefined,
    })
    if (sendSucceeded) {
      setTimeRemaining(pollTimeoutMs / 1000)
    }
  }

  const handleResendMagicLink = async () => {
    clearMessages()
    const sendSucceeded = await sendMagicLink({
      email: email.trim(),
      referral: referralCode.trim() || undefined,
    })
    if (sendSucceeded) {
      setTimeRemaining(pollTimeoutMs / 1000)
    }
  }

  const handleSendSms = async () => {
    if (!phone.trim()) {
      setError('Please enter a valid phone number')
      return
    }
    setIsLoading(true)
    clearMessages()
    try {
      const result = await sendSMS(phone.trim())
      if (result.kind === 'error') {
        setError(result.message || 'Unable to send SMS verification code.')
        return
      }

      await updateCurrentFlow(PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.PHONE)

      setSuccess('SMS code sent to your phone.')
      setCurrentStep('verify-phone')
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyPhone = async () => {
    if (!phoneCode.trim() || phoneCode.length !== 4) {
      setError('Please enter the 4-digit SMS verification code')
      return
    }
    setIsLoading(true)
    clearMessages()
    try {
      const result = await verifySMS(phoneCode.trim())
      if (result.kind === 'phone_already_claimed') {
        setError(
          'This phone number is already verified on another account. Please contact support@paytrie.com.'
        )
        return
      }
      if (result.kind === 'error') {
        if (result.message === 'Failed too many attempts. New SMS code sent') {
          setError('Too many attempts. A new SMS code has been sent.')
        } else if (result.message === 'Mismatched verification code') {
          setError('The verification code you entered does not match.')
        } else {
          setError(
            result.message ||
              'The verification code you entered does not match. Please try again.'
          )
        }
        return
      }

      await updateCurrentFlow(
        PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.VERIFY_PHONE
      )

      setSuccess('Phone verified successfully!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        setCurrentStep('personal-info')
        setSuccess(null)
        setIsProcessingSuccess(false)
      }, 1500)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handlePersonalInfoSubmit = async () => {
    const requiredFields = {
      firstName,
      lastName,
      dob,
      address1,
      city,
      province,
      postal,
    }
    for (const [key, value] of Object.entries(requiredFields)) {
      if (!value.trim()) {
        setError(
          `Please fill out the ${key.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`
        )
        return
      }
    }
    setIsLoading(true)
    clearMessages()
    try {
      const result = await patchPersonalInfo({
        firstName,
        lastName,
        dob,
        addressLine1: address1,
        addressLine2: address2,
        city,
        province,
        postalCode: postal,
      })

      if (result.ok === false) {
        setError(result.message)
        return
      }

      setSuccess('Personal information saved!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        setCurrentStep('occupation')
        setSuccess(null)
        setIsProcessingSuccess(false)
      }, 1500)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleOccupationSubmit = async () => {
    if (!employmentStatus.trim()) {
      setError('Please select your employment status.')
      return
    }

    // If employed or self-employed, occupation is required
    if (
      (employmentStatus === 'Employed' ||
        employmentStatus === 'Self-Employed') &&
      !occupation.trim()
    ) {
      setError('Please provide your occupation.')
      return
    }

    // If "Other" occupation selected, validate details
    if (occupation === 'Other') {
      const validation = validateOccupationDetails(occupationDetails)
      if (!validation.isValid) {
        setError(validation.error)
        return
      }
    }

    if (!intendedPurpose.trim()) {
      setError('Please select your intended purpose of account.')
      return
    }

    setIsLoading(true)
    clearMessages()
    try {
      const result = await patchOccupationInfo({
        employmentStatus,
        occupation,
        occupationDetails,
        purpose: intendedPurpose,
      })

      if (result.ok === false) {
        setError(result.message)
        return
      }

      const requirementsRes = await PaytrieAPI.getCurrentUserRequirements()
      if (requirementsRes.status !== 200) {
        setError('Unable to determine your signup progress. Please try again.')
        return
      }

      const nextStep =
        resolveStepFromRequirements(requirementsRes.data.data) ?? 'start-kyc'

      setSuccess('Employment information saved!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        if (nextStep === 'additional-info') {
          setNeedsAdditionalInfo(true)
        } else {
          setNeedsAdditionalInfo(false)
        }
        setCurrentStep(nextStep)
        setSuccess(null)
        setIsProcessingSuccess(false)
      }, 1500)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAdditionalInfoPage1Submit = async () => {
    const trimmed = {
      sourceOfFunds: sourceOfFunds.trim(),
      intendedUseOfUSDC: intendedUseOfUSDC.trim(),
      walletProvider: walletProvider.trim(),
      walletAddress: walletAddress.trim(),
    }

    if (
      !trimmed.sourceOfFunds ||
      !trimmed.intendedUseOfUSDC ||
      !trimmed.walletProvider ||
      !trimmed.walletAddress
    ) {
      setError('Please fill out all additional information fields.')
      return
    }

    setIsLoading(true)
    clearMessages()
    try {
      const result = await patchOlderUserInfo(
        trimmed,
        PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO
      )

      if (result.ok === false) {
        setError(result.message)
        return
      }

      setSuccess('Additional information saved!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        setCurrentStep('additional-info-2')
        setSuccess(null)
        setIsProcessingSuccess(false)
      }, 1500)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleAdditionalInfoPage2Submit = async () => {
    const trimmed = {
      howDidYouHear: howDidYouHear.trim(),
      whoReferredYou: whoReferredYou.trim(),
      previousCryptoExperience: previousCryptoExperience.trim(),
    }

    if (
      !trimmed.howDidYouHear ||
      !trimmed.whoReferredYou ||
      !trimmed.previousCryptoExperience
    ) {
      setError('Please fill out all additional information fields.')
      return
    }

    setIsLoading(true)
    clearMessages()
    try {
      const result = await patchOlderUserInfo(
        trimmed,
        PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.ADDITIONAL_INFO_PAGE2
      )

      if (result.ok === false) {
        setError(result.message)
        return
      }

      const requirementsRes = await PaytrieAPI.getCurrentUserRequirements()
      if (requirementsRes.status !== 200) {
        setError('Unable to determine your signup progress. Please try again.')
        return
      }

      const nextStep =
        resolveStepFromRequirements(requirementsRes.data.data) ?? 'start-kyc'

      setSuccess('Additional information saved!')
      setIsProcessingSuccess(true)
      setTimeout(() => {
        setCurrentStep(nextStep)
        setSuccess(null)
        setIsProcessingSuccess(false)
      }, 1500)
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleStartKyc = useCallback(async () => {
    setIsLoading(true)
    clearMessages()
    try {
      const result = await startKycRedirect()

      if (result.ok === true) {
        setVerificationUrl(result.url)
        localStorage.setItem('email', email.trim())
        localStorage.setItem('verificationLink', result.url)
      } else if (result.reason === 'already_complete') {
        window.location.href = '/Complete'
      } else {
        setError(result.message)
      }
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }, [clearMessages, email, handleApiError])

  const openVerification = useCallback(() => {
    if (!verificationUrl) return
    window.location.href = verificationUrl
  }, [verificationUrl])

  const renderEmailStep = () => {
    if (denied) {
      return (
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Sign-up canceled
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              The email verification was denied. If this was a mistake, you can
              try again.
            </p>
          </div>
          <Button
            onClick={resetDenied}
            className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
          >
            Try again
          </Button>
        </div>
      )
    }

    if (isCheckingEmail) {
      if (error) {
        return (
          <div className="space-y-4 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">{error}</p>
            <Button
              onClick={() => void retryAfterEmailVerified()}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
            >
              Try again
            </Button>
          </div>
        )
      }

      return (
        <div className="space-y-4 text-center">
          <RefreshCw className="w-10 h-10 text-green-600 dark:text-green-400 animate-spin mx-auto" />
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Verifying your email...
          </p>
        </div>
      )
    }

    if (waitingForApproval) {
      return (
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto">
              <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Check Your Email
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              We sent a secure verification link to{' '}
              <span className="font-medium">{email}</span>
            </p>
            {timeRemaining > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Link expires in:{' '}
                <span className="font-medium text-red-600 dark:text-red-400">
                  {formatTime(timeRemaining)}
                </span>
              </p>
            )}
          </div>

          <Card className="p-4 bg-green-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
            <div className="space-y-2">
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs">
                  Check your email inbox and spam folder
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs">
                  Open the link and tap Approve to continue signup here
                </p>
              </div>
              <div className="flex items-start space-x-2">
                <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <p className="text-xs">Keep this page open while you verify</p>
              </div>
            </div>
          </Card>

          <Button
            onClick={handleResendMagicLink}
            variant="outline"
            disabled={magicLinkLoading}
            className="w-full h-10"
          >
            Resend Verification Link
          </Button>
        </div>
      )
    }

    const displayedEmailError = emailError || magicLinkError

    return (
      <div className="space-y-2">
        <div className="text-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
            <Mail className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Create Account
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Enter your email address to get started
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="email">Email Address *</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailError) setEmailError('')
              }}
              placeholder="your@email.com"
              className={`w-full mt-1 ${displayedEmailError ? 'border-red-500 dark:border-red-500' : ''}`}
              disabled={magicLinkLoading}
              onKeyDown={(e) => e.key === 'Enter' && handleStartEmailSignup()}
            />
            {displayedEmailError && (
              <p className="text-xs text-red-500 mt-1">{displayedEmailError}</p>
            )}
          </div>
          <div>
            <Label htmlFor="referral">Referral Code (Optional)</Label>
            <Input
              id="referral"
              type="text"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              placeholder="Enter referral code"
              className="w-full mt-1"
              disabled={magicLinkLoading}
            />
          </div>
        </div>

        <Button
          onClick={handleStartEmailSignup}
          disabled={magicLinkLoading || !validateEmail(email).isValid}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
        >
          {magicLinkLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Sending Verification Link...</span>
            </>
          ) : (
            'Continue'
          )}
        </Button>

        <Card className="p-3 bg-blue-50 border-blue-200 dark:bg-blue-900 dark:border-blue-700">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            We&apos;ll email you a secure link. Open it and tap Approve to
            verify your email.
          </p>
        </Card>
      </div>
    )
  }

  const renderPhoneStep = () => (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
          <Phone className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Add Phone
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          We'll send a code to confirm your phone number
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(555) 123-4567"
            className="w-full mt-1"
            disabled={isLoading}
          />
        </div>
      </div>

      <Button
        onClick={handleSendSms}
        disabled={isLoading || !phone.trim()}
        className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            <span>Sending SMS...</span>
          </>
        ) : (
          'Send SMS Code'
        )}
      </Button>
    </div>
  )

  const renderVerifyPhoneStep = () => (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto">
          <Phone className="w-8 h-8 text-green-600 dark:text-green-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Verify Phone
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          We've sent a 4-digit code to{' '}
          <span className="font-medium">{phone}</span>
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <Label htmlFor="phoneCode">SMS Verification Code *</Label>
          <Input
            id="phoneCode"
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={phoneCode}
            onChange={(e) =>
              setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 4))
            }
            placeholder="1234"
            className="w-full text-center text-lg tracking-widest mt-1"
            disabled={isLoading}
            maxLength={4}
            autoComplete="one-time-code"
          />
        </div>
      </div>

      <div className="space-y-3">
        <Button
          onClick={handleVerifyPhone}
          disabled={isLoading || isProcessingSuccess || phoneCode.length !== 4}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Verifying...</span>
            </>
          ) : (
            'Verify Phone'
          )}
        </Button>
        <Button
          onClick={handleSendSms}
          variant="outline"
          disabled={isLoading}
          className="w-full h-10"
        >
          Resend SMS Code
        </Button>
      </div>
    </div>
  )

  const renderPersonalInfoStep = () => (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        {/* <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900 flex items-center justify-center mx-auto">
              <PersonStanding className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
            </div> */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
          Personal Info
        </h2>

        <p className="text-xs text-gray-600 dark:text-gray-400">
          {personalInfoStep === 1 ? 'Basic Information' : 'Address Information'}
        </p>

        {/* Step indicators */}
        <div className="flex justify-center space-x-2 mt-3">
          <div
            className={`w-2 h-2 rounded-full ${personalInfoStep === 1 ? 'bg-indigo-500' : 'bg-gray-300'}`}
          ></div>
          <div
            className={`w-2 h-2 rounded-full ${personalInfoStep === 2 ? 'bg-indigo-500' : 'bg-gray-300'}`}
          ></div>
        </div>
      </div>

      {personalInfoStep === 1 ? (
        // Step 1: Basic Information
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="firstName">Legal First Name *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                className="mt-1"
              />
              {firstName && !validateName(firstName).isValid && (
                <p className="text-xs text-red-500 mt-1">
                  {validateName(firstName).error}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="lastName">Legal Last Name *</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => handleLastNameChange(e.target.value)}
                className="mt-1"
              />
              {lastName && !validateName(lastName).isValid && (
                <p className="text-xs text-red-500 mt-1">
                  {validateName(lastName).error}
                </p>
              )}
            </div>
          </div>
          <div>
            <Label htmlFor="dobYear">Date of Birth *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="dobYear"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="YYYY"
                value={dobYear}
                onChange={(e) => handleYearChange(e.target.value)}
                className="flex-1 text-center"
                maxLength={4}
                autoComplete="bday-year"
              />
              <Input
                id="dobMonth"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="MM"
                value={dobMonth}
                onChange={(e) => handleMonthChange(e.target.value)}
                className="w-16 text-center"
                maxLength={2}
                autoComplete="bday-month"
              />
              <Input
                id="dobDay"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="DD"
                value={dobDay}
                onChange={(e) => handleDayChange(e.target.value)}
                className="w-16 text-center"
                maxLength={2}
                autoComplete="bday-day"
              />
            </div>
            {dobYear &&
              dobMonth &&
              dobDay &&
              !isValidDate(dobYear, dobMonth, dobDay) && (
                <p className="text-xs text-red-500 mt-1">
                  Please enter a valid date
                </p>
              )}
            {dobYear &&
              dobMonth &&
              dobDay &&
              isValidDate(dobYear, dobMonth, dobDay) &&
              (() => {
                const validation = validateAge(dobYear, dobMonth, dobDay)
                return !validation.isValid && validation.error ? (
                  <p className="text-xs text-red-500 mt-1">
                    {validation.error}
                  </p>
                ) : null
              })()}
          </div>
        </div>
      ) : (
        // Step 2: Address Information
        <div className="space-y-1">
          <div>
            <Label htmlFor="address1">Street Address *</Label>
            {isLoaded && !loadError ? (
              <div className="relative mt-1">
                <PlacesAutocompleteInput
                  value={address1}
                  onChange={setAddress1}
                  onSelect={handleAddressSelect}
                  placeholder="Start typing your address..."
                  searchOptions={getSearchOptions('CA')} // Canada for signup
                  error={false}
                  disabled={isParsingAddress}
                  className="w-full"
                />
                {isParsingAddress && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            ) : (
              <Input
                id="address1"
                value={address1}
                onChange={(e) => setAddress1(e.target.value)}
                placeholder={
                  loadError
                    ? 'Address lookup unavailable - enter manually'
                    : 'Loading address lookup...'
                }
                className="mt-1"
              />
            )}
            {loadError && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1 flex items-center">
                <Info className="w-3 h-3 mr-1" />
                Address autocomplete unavailable. Please enter address manually.
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="address2">Apartment, suite, etc. (Optional)</Label>
            <Input
              id="address2"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="postal">Postal Code *</Label>
              <Input
                id="postal"
                value={postal}
                onChange={(e) => setPostal(e.target.value)}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="province">Province *</Label>
            <Select value={province} onValueChange={setProvince}>
              <SelectTrigger id="province" className="w-full mt-1">
                <SelectValue placeholder="Select province" />
              </SelectTrigger>
              <SelectContent>
                {provinceOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex space-x-2">
        {personalInfoStep === 2 && (
          <Button
            onClick={() => setPersonalInfoStep(1)}
            variant="outline"
            className="flex-1"
          >
            Back
          </Button>
        )}
        <Button
          onClick={
            personalInfoStep === 1
              ? () => setPersonalInfoStep(2)
              : handlePersonalInfoSubmit
          }
          disabled={
            isLoading ||
            isProcessingSuccess ||
            (personalInfoStep === 1 &&
              (!firstName ||
                !lastName ||
                !dobYear ||
                !dobMonth ||
                !dobDay ||
                !isValidDate(dobYear, dobMonth, dobDay) ||
                !validateAge(dobYear, dobMonth, dobDay).isValid ||
                !validateName(firstName).isValid ||
                !validateName(lastName).isValid)) ||
            (personalInfoStep === 2 &&
              (!address1.trim() ||
                !city.trim() ||
                !province.trim() ||
                !postal.trim()))
          }
          className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Saving...</span>
            </>
          ) : personalInfoStep === 1 ? (
            'Next'
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    </div>
  )

  const renderOccupationStep = () => {
    const showOccupationField =
      employmentStatus === 'Employed' || employmentStatus === 'Self-Employed'
    const showOccupationDetails = occupation === 'Other'

    const canSubmit = () => {
      if (!employmentStatus) return false
      if (showOccupationField && !occupation) return false
      if (
        showOccupationDetails &&
        !validateOccupationDetails(occupationDetails).isValid
      )
        return false
      if (!validateAccountPurpose(intendedPurpose).isValid) return false
      return true
    }

    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Employment
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Provide employment and account purpose details
          </p>
        </div>

        <div className="space-y-3">
          {/* Employment Status */}
          <div>
            <Label className="text-sm font-medium uppercase mb-1 flex items-center gap-1">
              <Briefcase className="w-3 h-3" />
              Employment Status
            </Label>
            <RadioGroup
              value={employmentStatus}
              onValueChange={setEmploymentStatus}
              className="grid grid-cols-2 gap-2"
            >
              {employmentOptions.map((option) => (
                <div key={option} className="flex items-center space-x-2">
                  <RadioGroupItem value={option} id={`employment-${option}`} />
                  <Label htmlFor={`employment-${option}`} className="text-sm">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Occupation and Purpose in grid */}
          <div className="grid grid-cols-1 gap-3">
            {showOccupationField && (
              <div>
                <Label className="text-xs font-medium uppercase mb-1 block">
                  Occupation
                </Label>
                <Select value={occupation} onValueChange={setOccupation}>
                  <SelectTrigger className="w-full h-9">
                    <SelectValue placeholder="Select occupation" />
                  </SelectTrigger>
                  <SelectContent
                    side="bottom"
                    align="start"
                    className="max-h-60 overflow-y-auto"
                  >
                    {occupationOptions.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {showOccupationDetails && (
              <div>
                <Label className="text-xs font-medium uppercase mb-1 block">
                  Your occupation
                </Label>
                <Input
                  value={occupationDetails}
                  onChange={(e) => setOccupationDetails(e.target.value)}
                  placeholder="Job title (e.g., Software Engineer)"
                  className="w-full h-9"
                />
                {occupationDetails &&
                  !validateOccupationDetails(occupationDetails).isValid && (
                    <p className="text-xs text-red-500 mt-1">
                      {validateOccupationDetails(occupationDetails).error}
                    </p>
                  )}
              </div>
            )}

            <div>
              <Label className="text-xs font-medium uppercase mb-1 block">
                Account Purpose
              </Label>
              <Select
                value={intendedPurpose}
                onValueChange={setIntendedPurpose}
              >
                <SelectTrigger className="w-full h-9">
                  <SelectValue placeholder="Select purpose" />
                </SelectTrigger>
                <SelectContent>
                  {purposeOptions.map((option) => (
                    <SelectItem key={option} value={option}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Button
          onClick={handleOccupationSubmit}
          disabled={isLoading || isProcessingSuccess || !canSubmit()}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-10 font-medium"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    )
  }

  const renderAdditionalInfoStep = () => {
    const canSubmit =
      sourceOfFunds.trim() &&
      intendedUseOfUSDC.trim() &&
      walletProvider.trim() &&
      walletAddress.trim()

    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Additional Information
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Required for accounts opened by users aged 50 and over
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="sourceOfFunds">
              What is the source of funds for your transfer? *
            </Label>
            <Input
              id="sourceOfFunds"
              value={sourceOfFunds}
              onChange={(e) => setSourceOfFunds(e.target.value)}
              placeholder="Enter source of funds"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="intendedUseOfUSDC">
              What is the intended use of the purchased USDC? *
            </Label>
            <Input
              id="intendedUseOfUSDC"
              value={intendedUseOfUSDC}
              onChange={(e) => setIntendedUseOfUSDC(e.target.value)}
              placeholder="Enter intended use"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="walletProvider">
              What is your wallet provider? *
            </Label>
            <Input
              id="walletProvider"
              value={walletProvider}
              onChange={(e) => setWalletProvider(e.target.value)}
              placeholder="Enter wallet provider"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="walletAddress">
              What is your wallet address? *
            </Label>
            <Input
              id="walletAddress"
              value={walletAddress}
              onChange={(e) => setWalletAddress(e.target.value)}
              placeholder="Enter wallet address"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>
        </div>

        <Button
          onClick={handleAdditionalInfoPage1Submit}
          disabled={isLoading || !canSubmit}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    )
  }

  const renderAdditionalInfoStep2 = () => {
    const canSubmit =
      howDidYouHear.trim() &&
      whoReferredYou.trim() &&
      previousCryptoExperience.trim()

    return (
      <div className="space-y-4">
        <div className="text-center space-y-1">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Additional Information
          </h2>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            A few more details to complete your profile
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="howDidYouHear">
              How did you hear about Paytrie? *
            </Label>
            <Input
              id="howDidYouHear"
              value={howDidYouHear}
              onChange={(e) => setHowDidYouHear(e.target.value)}
              placeholder="Enter how you heard about us"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="whoReferredYou">
              Who referred you to Paytrie? *
            </Label>
            <Input
              id="whoReferredYou"
              value={whoReferredYou}
              onChange={(e) => setWhoReferredYou(e.target.value)}
              placeholder="Enter referral source"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>

          <div>
            <Label htmlFor="previousCryptoExperience">
              Have you previously purchased cryptocurrency? If so, for how long?
              *
            </Label>
            <Input
              id="previousCryptoExperience"
              value={previousCryptoExperience}
              onChange={(e) => setPreviousCryptoExperience(e.target.value)}
              placeholder="Enter your crypto experience"
              className="w-full mt-1"
              disabled={isLoading}
            />
          </div>
        </div>

        <Button
          onClick={handleAdditionalInfoPage2Submit}
          disabled={isLoading || !canSubmit}
          className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Saving...</span>
            </>
          ) : (
            'Continue'
          )}
        </Button>
      </div>
    )
  }

  const renderStartKycStep = () => {
    const mobile = isMobile()
    const showQR = !mobile && !!verificationUrl && !isLoading

    return (
      <div className="space-y-4 text-center">
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            ID Verification
          </h2>
          {!verificationUrl ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {isLoading
                ? 'Preparing verification...'
                : 'Complete your account setup with secure identity verification.'}
            </p>
          ) : showQR ? (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Scan the QR code below with your mobile device.
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Continue to complete identity verification on this device.
            </p>
          )}
        </div>

        <div className="space-y-4">
          {!verificationUrl && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 dark:text-gray-500">
                Please have a valid government-issued photo ID ready
                (driver&apos;s license, passport, etc.).
              </p>
            </div>
          )}

          {showQR && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-lg shadow-md">
                  <QRCode
                    size={200}
                    value={verificationUrl}
                    className="w-full h-auto"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Or continue on this device:
                </p>
                <Button
                  onClick={openVerification}
                  variant="outline"
                  className="w-full"
                >
                  Continue Verification on Current Device
                </Button>
              </div>
            </div>
          )}

          {mobile && verificationUrl && (
            <Button
              onClick={openVerification}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
            >
              Continue Verification
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {!verificationUrl && (
            <Button
              onClick={handleStartKyc}
              disabled={isLoading}
              className="w-full bg-green-600 hover:bg-green-700 text-white h-12 font-semibold"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  <span>Preparing...</span>
                </>
              ) : (
                'Start Verification'
              )}
            </Button>
          )}
        </div>
      </div>
    )
  }

  const getStepIndex = (step: SignUpStep) => {
    return stepOrder.indexOf(step)
  }

  const progressSteps: SignUpStep[] = needsAdditionalInfo
    ? [
        'email',
        'phone',
        'personal-info',
        'occupation',
        'additional-info',
        'start-kyc',
      ]
    : ['email', 'phone', 'personal-info', 'occupation', 'start-kyc']

  return (
    <div className="w-full max-w-md mx-auto pb-0 bg-white dark:bg-gray-900 rounded-lg shadow-xl overflow-hidden h-[600px] flex flex-col">
      <div className="p-4 sm:p-6 flex-1 flex flex-col overflow-hidden">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center">
            <button
              onClick={handleBackButton}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
            <h1 className="text-lg font-bold text-gray-900 dark:text-white ml-3">
              Sign Up
            </h1>
          </div>

          {/* Progress Dots */}
          <div className="flex items-center space-x-2">
            {progressSteps.map((step) => {
              const currentIndex = getStepIndex(currentStep)
              const stepIndex = getStepIndex(step)
              // Special handling for verify / multi-page steps
              const adjustedCurrentIndex =
                currentStep === 'verify-phone'
                  ? getStepIndex('phone')
                  : currentStep === 'additional-info-2'
                    ? getStepIndex('additional-info')
                    : currentIndex
              const isCompleted = adjustedCurrentIndex > stepIndex
              const isCurrent =
                (step === 'email' && currentStep === 'email') ||
                (step === 'phone' &&
                  (currentStep === 'phone' ||
                    currentStep === 'verify-phone')) ||
                (step === 'additional-info' &&
                  (currentStep === 'additional-info' ||
                    currentStep === 'additional-info-2')) ||
                step === currentStep

              return (
                <div
                  key={step}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    isCompleted
                      ? 'bg-green-500'
                      : isCurrent
                        ? 'bg-green-500'
                        : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              )
            })}
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500/30 flex items-start space-x-3">
            <AlertCircle className="w-3 h-3 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-500/30 flex items-start space-x-3">
            <CheckCircle className="w-3 h-3 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-green-800 dark:text-green-300">
              {success}
            </p>
          </div>
        )}

        <Card className="p-2 sm:p-4 dark:bg-gray-800 border-gray-200 dark:border-gray-700 flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            {currentStep === 'email' && renderEmailStep()}
            {currentStep === 'phone' && renderPhoneStep()}
            {currentStep === 'verify-phone' && renderVerifyPhoneStep()}
            {currentStep === 'personal-info' && renderPersonalInfoStep()}
            {currentStep === 'occupation' && renderOccupationStep()}
            {currentStep === 'additional-info' && renderAdditionalInfoStep()}
            {currentStep === 'additional-info-2' && renderAdditionalInfoStep2()}
            {currentStep === 'start-kyc' && renderStartKycStep()}
          </div>
        </Card>
      </div>

      <div className="text-center p-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Already have an account?{' '}
          <button
            onClick={onSignIn || onBack}
            className="font-semibold text-green-600 dark:text-green-400 hover:underline"
          >
            Sign In
          </button>
        </p>
      </div>
    </div>
  )
}

export default SignUpScreen
